import { Stack, Text, Title, useTheme } from "@bookingcom/bui-react";
import { SearchboxController, SearchBoxVerticalDefault } from "@bookingcom/flights-searchbox";
import { t } from "@bookingcom/lingojs-core";
import * as etV2 from "@flights/et-universal";
import { I18nChildContext, useI18n } from "@bookingcom/lingojs-react";
import FrequentlyAskedQuestions from "../components/elements/FrequentlyAskedQuestions";
import UspBlock from "../components/elements/UspBlock";
import React, { Profiler, useContext, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { trackMetaLandingsCustomGoalsV2 } from "../utils/experiments/track-meta-landings-v2";
import { Search, SearchOutboundMobile } from "../app/routes";
import AppShell from "../components/elements/AppShell";
import useClientMetrics from "../hooks/useClientMetrics";
import useGlobalContext from "../hooks/useGlobalContext";
import useLocaleContext from "../hooks/useLocaleContext";
import useSearchbox from "../hooks/useSearchbox";
import useTrackPageLoad from "../hooks/useTrackPageLoad";
import NoJS from "./NoJS";
import StaticContext from "../app/StaticContext/StaticContext";
import Frame from "../components/elements/Frame";
import Container from "../components/elements/Container";
import {
  RemoteAppleCardIndex,
  RemoteGeniusLaunchSheet,
  RemoteGeniusLoyalty,
  RemoteRewardsLandSheet
} from "../components/remote";
import PopularFlights from "../components/elements/PopularFlights";
import useLandingPageEvents from "../hooks/c360/useLandingPageEvents";
import HomeRecentSearchList from "../components/elements/HomeRecentSearch/HomeRecentSearchList";
import NoSSR from "../app/NoSSR";
import CrowdstrikeOutage from "../components/elements/CrowdstrikeOutage";
import { FlightsInterlinking } from "../components/elements/FlightsInterlinking";
import { goal, stage, trackWithDefaultStage } from "@flights/et-universal";
import useIsInViewport from "../hooks/useIsInViewport";
import EsimBanner from "../ancillaries/components/Esim/EsimBanner";
import { getEsimEligibilityIndex, getFetchStatusIndex } from "../ancillaries/store/esim/selectors";
import flights_ace_web_esim_banner, {
  useTrackEsimBannerStages
} from "../utils/experiments/ace/flights_ace_web_esim_banner";
import { useEsimIndex } from "../ancillaries/hooks/Esim/useEsimFetch";
import Wrapper from "../components/atoms/Wrapper";
import { getUserInfo } from "../store/user/selectors";
import { useSelector } from "react-redux";
import FlyAnywhereCountriesCarousel from "../components/elements/FlyAnywhere/FlyAnywhereCountriesCarousel";
import HomepageOffers from "../components/elements/HomepageOffers";
import { useSplitBoundsCondition } from "../utils/experiments/funnel/flights_split_bounds_us";
import useFlightsWebSouthwest from "../utils/experiments/funnel/flights_web_southwest";
import FlyAnywhereMapBanner from "../components/elements/FlyAnywhere/FlyAnywhereMap/FlyAnywhereMapBanner";
import BlackFridayBanner from "../components/elements/BlackFridayBanner/BlackFridayBanner";
import useBlackFridayExp from "../components/elements/BlackFridayBanner/useBlackFridayExp";

const Home = () => {
  const staticContext = useContext(StaticContext);
  const theme = useTheme();
  const i18n = useI18n();
  const trackContextEventV2 = useTrackPageLoad("index");
  const {
    userAgent: { isWebview }
  } = useGlobalContext();
  const searchBoxProps = useSearchbox();
  const { isRTL } = useLocaleContext();
  const { trackRenderTime } = useClientMetrics();
  const trackLandingPageEvents = useLandingPageEvents();
  /** start - flights_ace_web_esim_banner */
  const isEsimEligible = !!useSelector(getEsimEligibilityIndex);
  const esimFetchStatus = useSelector(getFetchStatusIndex);
  const showEsimBanner = isEsimEligible && !!flights_ace_web_esim_banner.trackWithDefaultStage();
  const fetchEsim = useEsimIndex();
  const esimBannerRef = useRef<HTMLDivElement>(null);
  const eSimInViewport = useIsInViewport(esimBannerRef.current);
  /** end - flights_ace_web_esim_banner */
  useTrackEsimBannerStages(isEsimEligible);
  const userInfo = useSelector(getUserInfo);
  const { isSplitBounds } = useSplitBoundsCondition();
  const flightsWebSouthwest = useFlightsWebSouthwest();

  if (process.env.BUILD_TARGET === "server" && staticContext.set) {
    const { getInitialState } = require("../server/screens/home");
    staticContext.set({ getInitialState });
  }

  /** start - flights_ace_web_esim_banner */
  useEffect(() => {
    if (esimFetchStatus === "initial") {
      void fetchEsim();
    }
  }, [esimFetchStatus, fetchEsim]);

  useEffect(() => {
    if (showEsimBanner && userInfo) {
      flights_ace_web_esim_banner.stages.with_booking();
    }
  }, [showEsimBanner, userInfo]);

  useEffect(() => {
    if (eSimInViewport && userInfo) {
      flights_ace_web_esim_banner.goals.viewed_esim_banner();
    }
  }, [eSimInViewport, userInfo]);
  /** end - flights_ace_web_esim_banner */

  useEffect(() => {
    flightsWebSouthwest.trackStages();
    Search.preload();
    /* start - flights_split_bounds_us */
    if (isSplitBounds) {
      SearchOutboundMobile.preload();
    }
    /* end - flights_split_bounds_us */
    trackContextEventV2();
    goal("flights_home_page_view");
    goal("tbu_vertical_home_page_view");
    trackLandingPageEvents("page_view", "homepage");
    stage("flights_web_cat_home_page_traffic_mdot_aa", 4);
    // track meta users going to index page
    trackMetaLandingsCustomGoalsV2(2, "mdot");
    stage("cm_post_trip_add_rewards_landsheet_to_flights_web", 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isBlackFridayExp = useBlackFridayExp();

  return (
    <Profiler id="screen" onRender={trackRenderTime}>
      <PageHeader />
      <AppShell noPadding>
        <Frame alignItems="center" direction="column" justifyContent="center" grow={1}>
          <Frame
            attributes={{
              style: {
                backgroundColor: isWebview
                  ? theme.colors.color_background_base
                  : theme.colors.color_background_disabled_alt,
                width: "100%"
              }
            }}
            pr={4}
            pl={4}
          >
            <NoJS />
            <Frame mt={4} mb={4}>
              <Stack gap={2}>
                <Text variant="headline_1" tagName="h1">
                  {i18n.trans(t("flights_index_header_compare_book_ease"))}
                </Text>
                {!isWebview && (
                  <span style={{ color: theme.colors.color_foreground_alt }}>
                    <Text variant="body_1">{i18n.trans(t("flights_index_header_discover_next_dream"))}</Text>
                  </span>
                )}
              </Stack>
            </Frame>
            <Frame mb={6}>
              <SearchboxController
                i18n={i18n}
                {...searchBoxProps}
                isRTL={isRTL}
                onSearch={(searchParams) => {
                  trackLandingPageEvents("click", "searchbox");
                  etV2.customGoal("mvsf_whs_launchpad_stays_flights_mdot", 2);
                  searchBoxProps.onSearch(searchParams);
                }}
                enabledCountriesAndAnywhere={true}
                allowEmptyDestination={true}
                isBlackFridayExp={isBlackFridayExp}
              >
                {() => <SearchBoxVerticalDefault />}
              </SearchboxController>
              <CrowdstrikeOutage />
            </Frame>
          </Frame>

          <NoSSR>
            <HomeRecentSearchList />
          </NoSSR>

          {isBlackFridayExp ? <BlackFridayBanner /> : undefined}

          <HomepageOffers isMobile={true} />
          {!!trackWithDefaultStage("flights_web_cat_fly_anywhere_map_view_mdot", 1) && (
            <Frame attributes={{ style: { width: "100%" } }} mt={4} p={4}>
              <FlyAnywhereMapBanner />
            </Frame>
          )}

          <Frame attributes={{ style: { width: "100%" } }} mt={4} p={4}>
            <FlyAnywhereCountriesCarousel />
          </Frame>

          <PopularFlights isMobile={true} />

          <RemoteAppleCardIndex />

          <RemoteGeniusLoyalty />

          {showEsimBanner && (
            <Container style={{ margin: `${theme.units.spacing_8x} auto`, alignItems: "start" }}>
              <Title title={i18n.trans(t("flights_genius_section_header"))} variant="headline_2" titleTagName="h2" />
              <Wrapper elementRef={esimBannerRef} noBorder noPadding>
                <EsimBanner context="index" />
              </Wrapper>
            </Container>
          )}

          <Frame attributes={{ style: { width: "100%" } }} mb={6}>
            <UspBlock />
          </Frame>

          <FlightsInterlinking />

          <Frame attributes={{ style: { width: "100%" } }} grow={1} p={4}>
            <FrequentlyAskedQuestions />
          </Frame>
        </Frame>
      </AppShell>
      <RemoteGeniusLaunchSheet />
      {trackWithDefaultStage("cm_post_trip_add_rewards_landsheet_to_flights_web", 1) ? (
        <RemoteRewardsLandSheet />
      ) : undefined}
    </Profiler>
  );
};

function PageHeader(props: { children?: React.ReactNode }) {
  const i18n = useI18n() as I18nChildContext;

  return (
    <Helmet>
      <title>
        {i18n.trans(
          t("mlx_flights_index_meta_title", {
            variables: { b_companyname: "Booking.com" }
          })
        )}
      </title>
      <meta name="description" content={i18n.trans(t("mlx_flights_index_meta_description"))} />
      <meta property="webview:header" content="regular" />
      <meta property="webview:title" content={""} />
      {props.children}
    </Helmet>
  );
}

export default Home;
export { PageHeader };
